import React, { useState, useEffect, useCallback, useRef } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2, Image, Upload, X } from "lucide-react";

export default function MediaLibraryPage() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const fileInputRef = useRef(null);

  const fetchMedia = useCallback(async () => {
    try { const { data } = await authAxios.get(`${API}/media`); setMedia(data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", uploadName.trim() || file.name);
        await authAxios.post(`${API}/upload/media-library`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      setUploadName("");
      fetchMedia();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const deleteMedia = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    await authAxios.delete(`${API}/media/${id}`);
    setMedia(media.filter(m => m.id !== id));
  };

  const filtered = filter === "all" ? media : media.filter(m => m.file_type === filter);

  if (loading) return <div className="p-8"><div className="animate-pulse h-64 bg-slate-200 rounded-xl" /></div>;

  return (
    <div className="p-8" data-testid="media-library-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#0f172a] flex items-center gap-2"><Image className="w-6 h-6" /> Media Library</h1>
        <p className="text-sm text-muted-foreground mt-1">Upload and manage media assets for flows and commands</p>
      </div>

      {/* Upload Zone */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${dragOver ? "border-[#22c55e] bg-green-50" : "border-slate-300 hover:border-slate-400"}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-dropzone"
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 text-[#22c55e] animate-spin" />
                <p className="text-sm text-muted-foreground">Uploading to Cloudinary...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-slate-400" />
                <p className="text-sm font-medium">Drag & drop files here or click to browse</p>
                <p className="text-xs text-muted-foreground">Supports images, audio, video, and documents (max 50MB)</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
            onChange={(e) => handleUpload(e.target.files)}
            data-testid="file-input"
          />
          <div className="flex gap-2 mt-3">
            <Input
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="Custom name (optional, uses filename if empty)"
              className="text-sm flex-1"
              data-testid="upload-name-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {["all","image","audio","video","document"].map(t => (
          <Button key={t} variant={filter===t?"default":"outline"} size="sm" onClick={() => setFilter(t)} className={filter===t?"bg-[#0f172a]":""} data-testid={`filter-${t}`}>
            {t === "all" ? "All" : t.charAt(0).toUpperCase()+t.slice(1)} ({t==="all"?media.length:media.filter(m=>m.file_type===t).length})
          </Button>
        ))}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">No media assets</CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map(m => (
            <Card key={m.id} data-testid={`media-${m.id}`}>
              <CardContent className="p-3">
                {m.file_type === "image" && m.file_url ? (
                  <img src={m.file_url} alt={m.name} className="w-full h-28 object-cover rounded mb-2 bg-slate-100" />
                ) : (
                  <div className="w-full h-28 rounded mb-2 bg-slate-100 flex items-center justify-center text-xs text-slate-400 uppercase">{m.file_type}</div>
                )}
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">{m.original_filename || m.file_url}</p>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="outline" className="text-[10px]">{m.file_type}</Badge>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteMedia(m.id)} data-testid={`delete-media-${m.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
